import { HStack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import { isIOS } from 'react-device-detect';

import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { tracking } from '#features/analytics';
import { isPwaOrTwa } from '#features/notifications/utils/isPwaOrTwa';
import { Button, GooglePlayIcon, Notice } from '#features/ui';
import { CloseButton } from '#features/ui/components/CloseButton';
import { envConfig } from '#constants/envConfig';
import { useSSR } from '#hooks/useSSR';
import { localStorage } from '#utils/localStorage';
import { playStoreUrl, type PlayStoreSource } from '../../utils/playStoreUrl';

// Ключ намеренно общий для всех мест показа: закрыв полоску в ленте, человек не должен
// встретить её же в чатах. Один отказ — один ответ на весь продукт.
const storageKey = (userId: string) => `android-app-banner-dismissed:${userId}`;

type Placement = Extract<PlayStoreSource, 'feed-banner' | 'discover-banner' | 'chats-banner'>;

type Props = {
  /** Куда именно встроена полоска — уезжает и в UTM, и в событие аналитики, и в текст. */
  placement: Placement;
};

/**
 * Тонкая полоска с предложением поставить Android-приложение (TWA-обёртка PWA).
 *
 * Не показывается: на сервере, без залогиненного пользователя, на iOS (приложения для
 * них ещё нет), внутри нативной оболочки, внутри уже установленного PWA/TWA и тем, кто
 * её однажды закрыл (запоминаем в localStorage по пользователю).
 *
 * `IS_NATIVE` проверяется отдельно от `isPwaOrTwa()`: тот хелпер смотрит на
 * `display-mode: standalone` и `android-app://` в referrer, а WebView Capacitor не даёт
 * ни того, ни другого — без этой проверки приложение звало установить само себя.
 */
export const AndroidAppBanner = ({ placement }: Props) => {
  const { isBrowser } = useSSR();
  const { profile } = useProfile();
  const userId = profile?.id;

  // Стартуем скрытыми и показываемся только после того, как эффект подтвердит (уже на
  // клиенте), что пользователь её не закрывал — иначе закрывшие ловят вспышку полоски.
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(userId ? localStorage.checkItem(storageKey(userId)) : true);
  }, [userId]);

  const hidden = !isBrowser || !userId || isIOS || envConfig.IS_NATIVE || isPwaOrTwa() || dismissed;

  // Показ считаем один раз за монтирование: без этого счётчика конверсию «увидел →
  // поставил» посчитать не из чего, а Play отдаёт только вторую половину воронки.
  const viewSent = useRef(false);
  useEffect(() => {
    if (hidden || viewSent.current) {
      return;
    }
    viewSent.current = true;
    tracking.event({
      action: 'app_install_banner_view',
      category: 'app_install',
      label: placement,
    });
  }, [hidden, placement]);

  const onClose = () => {
    if (userId) {
      localStorage.setItem(storageKey(userId), 'true');
    }
    setDismissed(true);
    tracking.event({
      action: 'app_install_banner_dismiss',
      category: 'app_install',
      label: placement,
    });
  };

  const onInstallClick = () => {
    tracking.event({
      action: 'app_install_banner_click',
      category: 'app_install',
      label: placement,
    });
  };

  if (hidden) {
    return null;
  }

  return (
    <AndroidAppBannerView placement={placement} onClose={onClose} onInstallClick={onInstallClick} />
  );
};

/** The same rendered banner, without profile, persistence or analytics side effects. */
export const AndroidAppBannerView = ({
  placement,
  onClose,
  onInstallClick,
}: Props & {
  onClose: () => void;
  onInstallClick: () => void;
}) => {
  const { t } = useLingui();
  // Сохраняем отдельное обещание уведомлений для чатов и приглашений для ленты.
  const message =
    placement === 'chats-banner'
      ? t`Chat and get instant notifications - install the Teamplay app for Android.`
      : t`Never miss a session invite - install the Teamplay app for Android.`;

  // В чатах это вложенное предложение, в ленте и поиске самостоятельная плашка.
  // Отступ от следующего блока сохраняем; поверхность и перенос действий задаёт Notice.
  const insideCard = placement === 'chats-banner';

  return (
    <Notice
      tone="accent"
      size={insideCard ? 'sm' : 'md'}
      boxShadow={insideCard ? undefined : 'card'}
      actionLayout="adaptive"
      icon={GooglePlayIcon}
      w="100%"
      flexShrink={0}
      mb={insideCard ? { base: 3, lg: 0 } : 3}
      action={
        <HStack w="100%" gap={2}>
          <Button asChild size="lg" flex={1} onClick={onInstallClick}>
            <a href={playStoreUrl(placement)} target="_blank" rel="noopener noreferrer">
              {t`Download`}
            </a>
          </Button>
          <CloseButton size="lg" flexShrink={0} onClick={onClose} />
        </HStack>
      }
    >
      <Text as="span" display={{ base: 'none', md: 'inline' }}>
        {message}
      </Text>
      <Text as="span" display={{ base: 'inline', md: 'none' }}>
        {t`Get the Teamplay Android app`}
      </Text>
    </Notice>
  );
};
